// AGC: FarmProHub Staking Hook
// AGC: Manages staking state and contract interactions
// AGC: Uses same StakingManagerV5 contract as Origami Battles

import { useState, useEffect, useCallback } from 'react'
import { useAccount, useReadContract, useWriteContract, useWaitForTransactionReceipt, usePublicClient } from 'wagmi'
import { parseAbi, formatEther, parseEther } from 'viem'

// AGC: StakingManagerV5 ABI (matches Origami Battles)
const STAKING_ABI = parseAbi([
  'function stake(uint256 amount) external',
  'function requestUnstake(uint256 amount) external',
  'function completeUnstake(uint256 requestIndex) external',
  'function getStakingInfo(address user) external view returns (uint256 stakedBalance, uint256 stakingScore, uint8 tier, uint256 pendingUnstakes)',
  'function getUnstakeRequest(address user, uint256 requestIndex) external view returns (uint256 amount, uint256 requestedAt, uint256 unlockTime, bool completed)',
  'function canAccessQuests(address user) external view returns (bool)',
  'function getMarketplaceDiscount(address user) external view returns (uint256)',
  'function isLocked(address user) external view returns (bool locked, uint256 unlockTime)',
  'function lockedUntil(address user) external view returns (uint256)',
  'function getStakedBalance(address user) external view returns (uint256)',
  'event Staked(address indexed user, uint256 amount, uint256 totalStaked, uint8 tier)',
  'event UnstakeRequested(address indexed user, uint256 amount, uint256 unlockTime)',
  'event Unstaked(address indexed user, uint256 amount, uint256 remainingStaked)',
])

// AGC: OGB Token ABI (for balance and approval)
const OGB_TOKEN_ABI = parseAbi([
  'function balanceOf(address account) external view returns (uint256)',
  'function allowance(address owner, address spender) external view returns (uint256)',
  'function approve(address spender, uint256 amount) external returns (bool)',
])

// AGC: Contract addresses from env
const STAKING_CONTRACT_ADDRESS = (import.meta.env.VITE_STAKING_MANAGER_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`
const OGB_TOKEN_ADDRESS = (import.meta.env.VITE_OGB_TOKEN_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`

export interface StakingStats {
  totalStaked: bigint
  totalStakers: number
  yourStake: bigint
  yourShare: number
  stakingScore: bigint
  tier: number
  pendingUnstakes: number
  isStaker: boolean
  isLoading: boolean
}

export interface UseStakingReturn {
  stats: StakingStats
  stake: (amount: bigint) => Promise<void>
  requestUnstake: (amount: bigint) => Promise<void>
  completeUnstake: (requestIndex: number) => Promise<void>
  ogbBalance: bigint
  isStaking: boolean
  isUnstaking: boolean
  refresh: () => void
}

export const useStaking = (): UseStakingReturn => {
  const { address, isConnected } = useAccount()
  const publicClient = usePublicClient()
  const [isRefreshing, setIsRefreshing] = useState(false)

  // AGC: Read total staked (OGB balance of StakingManager contract)
  // AGC: This is public data, should be readable even without wallet connected
  const { data: totalStaked, refetch: refetchTotalStaked } = useReadContract({
    address: OGB_TOKEN_ADDRESS,
    abi: OGB_TOKEN_ABI,
    functionName: 'balanceOf',
    args: STAKING_CONTRACT_ADDRESS !== '0x0000000000000000000000000000000000000000' ? [STAKING_CONTRACT_ADDRESS] : undefined,
    query: {
      enabled: STAKING_CONTRACT_ADDRESS !== '0x0000000000000000000000000000000000000000', // AGC: Removed isConnected requirement - public data
    },
  })

  // AGC: Read user's staking info (includes stakedBalance, stakingScore, tier, pendingUnstakes)
  const { data: stakingInfo, refetch: refetchStakingInfo } = useReadContract({
    address: STAKING_CONTRACT_ADDRESS,
    abi: STAKING_ABI,
    functionName: 'getStakingInfo',
    args: address ? [address] : undefined,
    query: {
      enabled: !!STAKING_CONTRACT_ADDRESS && !!address && STAKING_CONTRACT_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  })

  // AGC: Read user's OGB balance
  const { data: ogbBalance, refetch: refetchOgbBalance } = useReadContract({
    address: OGB_TOKEN_ADDRESS,
    abi: OGB_TOKEN_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address && OGB_TOKEN_ADDRESS !== '0x0000000000000000000000000000000000000000',
    },
  })

  // AGC: Extract staking info
  const yourStake = stakingInfo ? (stakingInfo as any)[0] as bigint : 0n
  const stakingScore = stakingInfo ? (stakingInfo as any)[1] as bigint : 0n
  const tier = stakingInfo ? Number((stakingInfo as any)[2]) : 0
  const pendingUnstakes = stakingInfo ? Number((stakingInfo as any)[3]) : 0

  // AGC: Calculate your share
  const yourShare = totalStaked && yourStake && totalStaked > 0n
    ? Number((yourStake * 10000n) / totalStaked) / 100
    : 0

  // AGC: Check if user is a staker (has any stake)
  const isStaker = yourStake > 0n

  // AGC: Write contract functions
  const { writeContractAsync, isPending: isWritePending } = useWriteContract()

  // AGC: Stake function (requires OGB approval first)
  const stake = useCallback(async (amount: bigint) => {
    if (!STAKING_CONTRACT_ADDRESS || !address || STAKING_CONTRACT_ADDRESS === '0x0000000000000000000000000000000000000000') {
      throw new Error('Staking contract not configured or wallet not connected')
    }

    // AGC: Check and approve if needed
    const allowance = await publicClient?.readContract({
      address: OGB_TOKEN_ADDRESS,
      abi: OGB_TOKEN_ABI,
      functionName: 'allowance',
      args: [address, STAKING_CONTRACT_ADDRESS],
    }) as bigint | undefined

    if (!allowance || allowance < amount) {
      // AGC: Approve first
      await writeContractAsync({
        address: OGB_TOKEN_ADDRESS,
        abi: OGB_TOKEN_ABI,
        functionName: 'approve',
        args: [STAKING_CONTRACT_ADDRESS, amount],
      })
    }

    // AGC: Stake
    await writeContractAsync({
      address: STAKING_CONTRACT_ADDRESS,
      abi: STAKING_ABI,
      functionName: 'stake',
      args: [amount],
    })
  }, [address, writeContractAsync, publicClient])

  // AGC: Request unstake function
  const requestUnstake = useCallback(async (amount: bigint) => {
    if (!STAKING_CONTRACT_ADDRESS || !address || STAKING_CONTRACT_ADDRESS === '0x0000000000000000000000000000000000000000') {
      throw new Error('Staking contract not configured or wallet not connected')
    }

    await writeContractAsync({
      address: STAKING_CONTRACT_ADDRESS,
      abi: STAKING_ABI,
      functionName: 'requestUnstake',
      args: [amount],
    })
  }, [address, writeContractAsync])

  // AGC: Complete unstake function
  const completeUnstake = useCallback(async (requestIndex: number) => {
    if (!STAKING_CONTRACT_ADDRESS || !address || STAKING_CONTRACT_ADDRESS === '0x0000000000000000000000000000000000000000') {
      throw new Error('Staking contract not configured or wallet not connected')
    }

    await writeContractAsync({
      address: STAKING_CONTRACT_ADDRESS,
      abi: STAKING_ABI,
      functionName: 'completeUnstake',
      args: [BigInt(requestIndex)],
    })
  }, [address, writeContractAsync])

  // AGC: Refresh all data
  const refresh = useCallback(() => {
    setIsRefreshing(true)
    Promise.all([
      refetchTotalStaked(),
      refetchStakingInfo(),
      refetchOgbBalance(),
    ]).finally(() => setIsRefreshing(false))
  }, [refetchTotalStaked, refetchStakingInfo, refetchOgbBalance])

  return {
    stats: {
      totalStaked: totalStaked ?? 0n,
      totalStakers: 0, // AGC: Not available in StakingManagerV5, would need separate tracking
      yourStake,
      yourShare,
      stakingScore,
      tier,
      pendingUnstakes,
      isStaker,
      isLoading: isRefreshing,
    },
    stake,
    requestUnstake,
    completeUnstake,
    ogbBalance: ogbBalance ?? 0n,
    isStaking: isWritePending,
    isUnstaking: isWritePending,
    refresh,
  }
}

